import { GoogleProfileData } from '../types';

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export interface GoogleUserInfo {
  id: string;
  email: string;
  name: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  locale?: string;
  verified_email: boolean;
}

export interface GoogleOAuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    userType: 'freelancer' | 'client';
  };
  message: string;
  is_new_user: boolean;
  google_user_info: GoogleUserInfo;
}

class GoogleAuthService {
  private clientId: string;
  private isLoaded: boolean = false;

  constructor() {
    this.clientId = GOOGLE_CLIENT_ID;
    this.loadGoogleScript();
  }

  private loadGoogleScript(): void {
    if (this.isLoaded) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.isLoaded = true;
    };
    document.head.appendChild(script);
  }

  async initializeGoogleAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isLoaded) {
        const checkLoaded = setInterval(() => {
          if (this.isLoaded) {
            clearInterval(checkLoaded);
            this.setupGoogleAuth();
            resolve();
          }
        }, 100);
        return;
      }
      this.setupGoogleAuth();
      resolve();
    });
  }

  private setupGoogleAuth(): void {
    if (typeof window !== 'undefined' && (window as any).google) {
      (window as any).google.accounts.id.initialize({
        client_id: this.clientId,
        callback: () => {}, // Will be handled by individual methods
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    }
  }

  async signInWithGoogle(): Promise<GoogleOAuthResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isLoaded || !(window as any).google) {
        reject(new Error('Google OAuth not loaded'));
        return;
      }

      (window as any).google.accounts.oauth2.initTokenClient({
        client_id: this.clientId,
        scope: 'openid email profile https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/user.phonenumbers.read https://www.googleapis.com/auth/user.birthday.read https://www.googleapis.com/auth/user.addresses.read https://www.googleapis.com/auth/user.organization.read https://www.googleapis.com/auth/user.education.read',
        callback: async (response: any) => {
          try {
            const result = await this.authenticateWithBackend(response.access_token);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
      }).requestAccessToken();
    });
  }

  async getGoogleProfileData(accessToken: string): Promise<GoogleProfileData> {
    try {
      const response = await fetch('/auth/google/profile-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to get Google profile data');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting Google profile data:', error);
      throw error;
    }
  }

  async registerFreelancerWithGoogle(accessToken: string): Promise<any> {
    try {
      const response = await fetch('/auth/google/register-freelancer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to register with Google');
      }

      return await response.json();
    } catch (error) {
      console.error('Error registering with Google:', error);
      throw error;
    }
  }

  private async authenticateWithBackend(accessToken: string): Promise<GoogleOAuthResponse> {
    try {
      const response = await fetch('/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Google authentication failed');
      }

      const data = await response.json();
      
      // Ensure userType is properly typed
      return {
        ...data,
        user: {
          ...data.user,
          userType: data.user.userType as 'freelancer' | 'client'
        }
      };
    } catch (error) {
      console.error('Error authenticating with backend:', error);
      throw error;
    }
  }

  // Utility method to check if Google OAuth is available
  isGoogleOAuthAvailable(): boolean {
    return this.isLoaded && !!(window as any).google && !!this.clientId;
  }

  // Method to get Google user info from token
  async getGoogleUserInfo(accessToken: string): Promise<GoogleUserInfo> {
    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${accessToken}`);
      if (!response.ok) {
        throw new Error('Failed to get user info from Google');
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting Google user info:', error);
      throw error;
    }
  }
}

export const googleAuthService = new GoogleAuthService();
